import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Plus, Calendar, MapPin, Users, QrCode, Scan, Settings, Archive, ArchiveRestore, Trash2, UserCircle, HelpCircle, CopyPlus, Ticket, Bell, X, Clock, BarChart3, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import QuickStartOverlay from '../components/QuickStartOverlay';
import SafeImage from '../components/SafeImage';
import ActivityFeedCard from '../components/activity/ActivityFeedCard';
import HostApprovalBanner from '../components/HostApprovalBanner';
import LastEventSummaryCard from '../components/dashboard/LastEventSummaryCard';
import StripePayoutSummaryCard from '../components/StripePayoutSummaryCard';
import { getEventImageSrc } from '../lib/defaultBranding';
import { formatEventDateTime, formatEventTimeRange } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function isDatePast(dateStr) {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(dateStr + 'T23:59:59');
  return eventDate < today;
}

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [staffEvents, setStaffEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [hasDefaults, setHasDefaults] = useState(true);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState({ total: 0, groups: [] });
  const [showBellPanel, setShowBellPanel] = useState(false);

  useEffect(() => { fetchEvents(); checkDefaults(); fetchPendingApprovals(); }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [eventsRes, staffRes] = await Promise.all([
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/staff/assigned`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setEvents(eventsRes.data);
      setStaffEvents(staffRes.data);
      // Trigger quickstart for first-time hosts with 0 events
      if (eventsRes.data.length === 0 && !localStorage.getItem('vg_quickstart_seen')) {
        setTimeout(() => setShowQuickStart(true), 600);
      }
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const checkDefaults = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [pmRes, tierRes] = await Promise.all([
        axios.get(`${API}/users/payment-methods`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/pass-tiers`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setHasDefaults(pmRes.data.length > 0 || tierRes.data.length > 0);
    } catch { setHasDefaults(true); }
  };

  const fetchPendingApprovals = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/host/pending-approvals`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingApprovals(res.data);
    } catch { /* ignore */ }
  };

  const relativeTime = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };


  const handleArchive = async (eventId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('session_token');
      await axios.patch(`${API}/events/${eventId}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event archived');
      fetchEvents();
    } catch { toast.error('Failed to archive'); }
  };

  const handleUnarchive = async (eventId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('session_token');
      await axios.patch(`${API}/events/${eventId}/unarchive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event restored');
      fetchEvents();
    } catch { toast.error('Failed to unarchive'); }
  };

  const handleDelete = async (eventId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event permanently deleted');
      setDeleteConfirm(null);
      fetchEvents();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDuplicate = async (eventId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/events/${eventId}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event duplicated');
      navigate(`/events/${res.data.event_id}/edit`);
    } catch { toast.error('Failed to duplicate'); }
  };

  // Categorize events
  const { activeEvents, pastEvents, archivedEvents } = useMemo(() => {
    const active = [];
    const past = [];
    const archived = [];
    events.forEach(evt => {
      if ((evt.status || 'active') === 'archived') {
        archived.push(evt);
      } else if (isDatePast(evt.date)) {
        past.push(evt);
      } else {
        active.push(evt);
      }
    });
    return { activeEvents: active, pastEvents: past, archivedEvents: archived };
  }, [events]);

  const displayedEvents = tab === 'active' ? activeEvents : tab === 'past' ? pastEvents : archivedEvents;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-[#EAE7E1] tracking-tight">Dashboard</h1>
                <p className="mt-1 text-base text-[#AFAFAF]">Manage your VelvetGate events</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <Button onClick={() => setShowBellPanel(!showBellPanel)} variant="ghost" className="text-[#AFAFAF] hover:text-[#EAE7E1] relative" data-testid="approvals-bell-button">
                    <Bell className="w-5 h-5" />
                    {pendingApprovals.total > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#C44545] text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="approvals-bell-badge">{pendingApprovals.total}</span>
                    )}
                  </Button>
                </div>
                <Button onClick={() => navigate('/calendar')} variant="ghost" className="text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="calendar-nav-button"><Calendar className="w-5 h-5" /></Button>
                <Button onClick={() => navigate('/account')} variant="ghost" className="text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="account-button"><UserCircle className="w-5 h-5" /></Button>
                <Button onClick={() => navigate('/events/create')} className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-4 sm:px-6 py-3 font-bold" data-testid="create-event-button"><Plus className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Create </span>Event</Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/my-tickets')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs sm:text-sm h-8" data-testid="my-passes-button"><Ticket className="w-3.5 h-3.5 mr-1.5" />My Passes</Button>
              <Button onClick={() => navigate('/payment-methods')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs sm:text-sm h-8" data-testid="payment-methods-button">Payment Methods</Button>
              <Button onClick={() => navigate('/pass-tiers')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs sm:text-sm h-8" data-testid="pass-tiers-button">Pass Tiers</Button>
              <Button onClick={() => navigate('/reports')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs sm:text-sm h-8" data-testid="reports-nav-button"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Reports</Button>
              <Button onClick={() => navigate('/guide')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs sm:text-sm h-8" data-testid="guide-nav-button"><BookOpen className="w-3.5 h-3.5 mr-1.5" />Guide</Button>
              {user?.role === 'admin' && (
                <Button onClick={() => navigate('/settings/platform')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs sm:text-sm h-8" data-testid="settings-button"><Settings className="w-3.5 h-3.5 mr-1.5" />Settings</Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* One-time welcome banner for newly-approved hosts */}
      <HostApprovalBanner user={user} />

      {/* Approvals Bell Panel */}
      {showBellPanel && (
        <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]" data-testid="approvals-panel">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-bold text-[#EAE7E1]">Action Required</h2>
                <p className="text-xs text-[#777]">{pendingApprovals.total} pending approval{pendingApprovals.total !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setShowBellPanel(false)} className="text-[#555] hover:text-[#EAE7E1]" data-testid="close-approvals-panel"><X className="w-4 h-4" /></button>
            </div>
            {pendingApprovals.total === 0 ? (
              <div className="text-center py-6 text-sm text-[#555]" data-testid="approvals-empty">All clear. You're good to go.</div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {pendingApprovals.groups.map(group => (
                  <div key={group.event_id}>
                    <button onClick={() => { setShowBellPanel(false); navigate(`/events/${group.event_id}/manage`); }} className="text-xs font-semibold text-[#D4AF37] hover:text-[#F2D27A] mb-1.5 transition-colors" data-testid={`approval-group-${group.event_id}`}>
                      {group.event_title} ({group.items.length})
                    </button>
                    <div className="space-y-1">
                      {group.items.map(item => (
                        <button key={item.ticket_id} onClick={() => { setShowBellPanel(false); navigate(`/events/${group.event_id}/manage`); }} className="w-full flex items-center justify-between p-2 rounded-sm bg-[#050505] border border-[#1A1A1A] hover:border-[#333] transition-colors text-left" data-testid={`approval-item-${item.ticket_id}`}>
                          <div className="min-w-0 flex-1">
                            <span className="text-sm text-[#EAE7E1]">{item.guest_name}</span>
                            {item.pass_tier_name && <span className="text-xs text-[#777] ml-2">{item.pass_tier_name}</span>}
                          </div>
                          <span className="text-[10px] text-[#555] flex-shrink-0 ml-2 flex items-center"><Clock className="w-3 h-3 mr-0.5" />{relativeTime(item.submitted_at)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Recent Activity card (host/admin-only) */}
        <div className="mb-8">
          <ActivityFeedCard limit={10} defaultCollapsed={true} />
        </div>

        {/* Stripe Payouts read-only summary (host only — admins also OK).
            Component self-margins so when it returns null for non-Stripe hosts
            the layout closes up cleanly with no orphan vertical gap. */}
        <StripePayoutSummaryCard />

        {/* Last Event Summary — compact snapshot of the most recent completed event */}
        <LastEventSummaryCard events={events} />

        {/* Tabs */}
        <div className="flex space-x-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-1 mb-8 w-fit" data-testid="event-tabs">
          {[
            ['active', 'Active', activeEvents.length],
            ['past', 'Past', pastEvents.length],
            ['archived', 'Archived', archivedEvents.length]
          ].map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-5 py-2 rounded-sm text-sm font-semibold transition-colors ${tab === key ? 'bg-[#D4AF37] text-[#050505]' : 'text-[#AFAFAF] hover:text-[#EAE7E1]'}`}
              data-testid={`tab-${key}`}
            >
              {label} <span className={`ml-1.5 text-xs ${tab === key ? 'text-[#050505]/70' : 'text-[#555]'}`}>({count})</span>
            </button>
          ))}
        </div>

        {displayedEvents.length === 0 && staffEvents.length === 0 && tab === 'active' ? (
          <div className="text-center py-24" data-testid="empty-state">
            <QrCode className="w-16 h-16 mx-auto text-[#1A1A1A] mb-6" />
            <h3 className="text-xl font-semibold text-[#EAE7E1] mb-2">No active events</h3>
            <p className="text-[#AFAFAF] mb-8">Create your first event to start selling passes</p>
            <div className="flex flex-col items-center gap-3">
              <Button onClick={() => navigate('/events/create')} className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] font-bold px-8 py-3 rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> Create Event
              </Button>
              {!hasDefaults && (
                <button onClick={() => navigate('/setup')} className="text-sm text-[#C6A66B] hover:text-[#D4AF37] transition-colors flex items-center gap-1.5" data-testid="setup-defaults-cta">
                  <Settings className="w-3.5 h-3.5" /> Set Up My Event Defaults
                </button>
              )}
              {!showQuickStart && (
                <button onClick={() => { localStorage.removeItem('vg_quickstart_seen'); setShowQuickStart(true); }} className="text-xs text-[#555] hover:text-[#B9B1A6] transition-colors flex items-center gap-1" data-testid="relaunch-quickstart">
                  <HelpCircle className="w-3 h-3" /> Need help getting started?
                </button>
              )}
            </div>
          </div>
        ) : displayedEvents.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-tab-state">
            <p className="text-[#AFAFAF]">No {tab} events</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedEvents.map((event) => (
              <div
                key={event.event_id}
                className={`bg-[#0A0A0A] border rounded-sm overflow-hidden hover:border-[#D4AF37]/40 transition-all duration-150 cursor-pointer ${tab === 'archived' ? 'border-[#1A1A1A] opacity-70' : 'border-[#1A1A1A]'}`}
                onClick={() => navigate(`/events/${event.event_id}/manage`)}
                data-testid={`event-card-${event.event_id}`}
              >
                {event.event_image_url ? (
                  <SafeImage src={`${BACKEND_URL}${event.event_image_url}`} alt={event.title} className="w-full h-40 object-cover" fallbackLabel={event.title?.slice(0,2)?.toUpperCase() || 'EVENT'} fallbackClassName="w-full h-40" />
                ) : (
                  <SafeImage src={getEventImageSrc(event)} alt="VelvetGate" className="w-full h-40 object-cover" fallbackLabel={event.title?.slice(0,2)?.toUpperCase() || 'EVENT'} fallbackClassName="w-full h-40" data-testid="event-card-default-image" />
                )}
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-[#EAE7E1] mb-2 tracking-tight">{event.title}</h3>
                  <div className="space-y-1.5 text-sm text-[#AFAFAF]">
                    <div className="flex items-center"><Calendar className="w-4 h-4 mr-2 text-[#D4AF37]" />{formatEventTimeRange(event)}</div>
                    <div className="flex items-center"><MapPin className="w-4 h-4 mr-2 text-[#D4AF37]" />{event.location}</div>
                    <div className="flex items-center" data-testid={`event-card-capacity-${event.event_id}`}><Users className="w-4 h-4 mr-2 text-[#D4AF37]" />{event.is_unlimited_capacity ? 'Unlimited capacity' : `Capacity: ${event.capacity}`}</div>
                  </div>
                  <div className="mt-4 flex space-x-2">
                    <Button onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.event_id}/manage`); }} className="flex-1 bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-semibold" data-testid={`manage-event-${event.event_id}`}>Manage</Button>
                    {tab !== 'archived' && (
                      <Button onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.event_id}/edit`); }} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid={`edit-event-${event.event_id}`}>Edit</Button>
                    )}
                  </div>
                  {/* Lifecycle actions */}
                  <div className="mt-2 flex space-x-2">
                    <button onClick={(e) => handleDuplicate(event.event_id, e)} className="flex items-center text-xs text-[#777] hover:text-[#D4AF37] transition-colors" data-testid={`duplicate-event-${event.event_id}`}>
                      <CopyPlus className="w-3.5 h-3.5 mr-1" /> Duplicate
                    </button>
                    {tab !== 'archived' ? (
                      <button onClick={(e) => handleArchive(event.event_id, e)} className="flex items-center text-xs text-[#777] hover:text-[#AFAFAF] transition-colors" data-testid={`archive-event-${event.event_id}`}>
                        <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                      </button>
                    ) : (
                      <button onClick={(e) => handleUnarchive(event.event_id, e)} className="flex items-center text-xs text-[#D4AF37] hover:text-[#F2D27A] transition-colors" data-testid={`unarchive-event-${event.event_id}`}>
                        <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Unarchive
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(event.event_id); }} className="flex items-center text-xs text-[#777] hover:text-[#C44545] transition-colors" data-testid={`delete-event-${event.event_id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Staff-Assigned Events */}
        {staffEvents.length > 0 && tab === 'active' && (
          <div className="mt-12">
            <div className="flex items-center space-x-3 mb-6">
              <Scan className="w-6 h-6 text-[#D4AF37]" />
              <div>
                <h2 className="text-2xl font-bold text-[#EAE7E1] tracking-tight">Check-In Staff</h2>
                <p className="text-sm text-[#AFAFAF]">Events you're assigned to as check-in staff</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {staffEvents.map((event) => (
                <div
                  key={event.event_id}
                  className="bg-[#0A0A0A] border-2 border-[#D4AF37]/30 rounded-sm overflow-hidden hover:border-[#D4AF37]/60 transition-all duration-150 cursor-pointer"
                  onClick={() => navigate(`/events/${event.event_id}/check-in`)}
                  data-testid={`staff-event-card-${event.event_id}`}
                >
                  {event.event_image_url ? (
                    <SafeImage src={`${BACKEND_URL}${event.event_image_url}`} alt={event.title} className="w-full h-40 object-cover" fallbackLabel={event.title?.slice(0,2)?.toUpperCase() || 'EVENT'} fallbackClassName="w-full h-40" />
                  ) : (
                    <SafeImage src={getEventImageSrc(event)} alt="VelvetGate" className="w-full h-40 object-cover" fallbackLabel={event.title?.slice(0,2)?.toUpperCase() || 'EVENT'} fallbackClassName="w-full h-40" data-testid="staff-event-card-default-image" />
                  )}
                  <div className="p-5">
                    <div className="inline-block px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider rounded-sm mb-2">Check-In Staff</div>
                    <h3 className="text-lg font-semibold text-[#EAE7E1] mb-2 tracking-tight">{event.title}</h3>
                    <div className="space-y-1.5 text-sm text-[#AFAFAF]">
                      <div className="flex items-center"><Calendar className="w-4 h-4 mr-2 text-[#D4AF37]" />{formatEventTimeRange(event)}</div>
                      <div className="flex items-center"><MapPin className="w-4 h-4 mr-2 text-[#D4AF37]" />{event.location}</div>
                    </div>
                    <Button onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.event_id}/check-in`); }} className="w-full mt-4 bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid={`staff-checkin-${event.event_id}`}>
                      <Scan className="w-4 h-4 mr-2" /> Open Scanner
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)} data-testid="delete-confirm-modal">
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#EAE7E1] mb-2">Delete Event?</h3>
            <p className="text-sm text-[#AFAFAF] mb-1">This will permanently delete the event and all associated tickets, discount codes, and data.</p>
            <p className="text-sm text-[#C44545] font-medium mb-6">This action cannot be undone. Consider archiving instead.</p>
            <div className="flex space-x-3">
              <Button onClick={() => setDeleteConfirm(null)} variant="outline" className="flex-1 border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="cancel-delete-button">Cancel</Button>
              <Button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-[#C44545] hover:bg-[#E11D48] text-white rounded-sm font-bold" data-testid="confirm-delete-button">
                <Trash2 className="w-4 h-4 mr-2" /> Delete Permanently
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Start Overlay */}
      {showQuickStart && (
        <QuickStartOverlay onDismiss={() => setShowQuickStart(false)} />
      )}
    </div>
  );
}
